import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Download, FileText, Menu, Database, Eye, ChevronDown, Wrench } from 'lucide-react';
import PdfPreviewModal from './PdfPreviewModal';
import ChartSelectionModal from './ChartSelectionModal';
import { useApp } from '../context/AppContext';

import { RATING_LABELS, calculateTrafficLights } from '../utils/trafficLight';
import { mapExportRows } from '../utils/dataProcessor/helpers';
import { filterResults } from '../utils/dataProcessor/filters';

export default function Header({ onToggleSidebar, requestChartCapture, setExportRequest }) {
  const { programInfo, sections, stats, filters, isExporting, setExporting, reportType, reportMode, trafficLights, filteredResults, tocData, activeSection, sectionTabs, resultsData } = useApp();
  const [dataExporting, setDataExporting] = useState(false);
  const [pdfPreview, setPdfPreview] = useState({ open: false, url: null, filename: null });
  const [chartModal, setChartModal] = useState({ open: false, format: null });
  const [docxMenuOpen, setDocxMenuOpen] = useState(false);
  const [pdfMenuOpen, setPdfMenuOpen] = useState(false);
  const [devMenuOpen, setDevMenuOpen] = useState(false);
  const docxMenuRef = useRef(null);
  const pdfMenuRef = useRef(null);
  const devMenuRef = useRef(null);

  useEffect(() => {
    if (!docxMenuOpen && !pdfMenuOpen && !devMenuOpen) return;
    const handleClick = (e) => {
      if (docxMenuRef.current && !docxMenuRef.current.contains(e.target)) setDocxMenuOpen(false);
      if (pdfMenuRef.current && !pdfMenuRef.current.contains(e.target)) setPdfMenuOpen(false);
      if (devMenuRef.current && !devMenuRef.current.contains(e.target)) setDevMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [docxMenuOpen, pdfMenuOpen, devMenuOpen]);

  // Build areaOfWorkProgress array from dynamic aowNarrative_* section keys + traffic light data
  const buildAowProgress = () => {
    if (!trafficLights?.computed?.length) return sections.areaOfWorkProgress || [];
    return trafficLights.computed.map(aow => {
      const id = aow.name.replace(/\s+/g, '-').toLowerCase();
      const narrative = sections[`aowNarrative_${id}`] || '';
      return {
        id,
        name: aow.name,
        rating: aow.rating,
        narrative,
        highlightBox: sections[`aow_${id}HighlightBox`] || '',
        quote: sections[`aow_${id}Quote`] || '',
        quoteAttribution: sections[`aow_${id}QuoteAttribution`] || '',
        resourceLinks: sections[`aowNarrativeLinks_${id}`] || [],
      };
    });
  };

  const buildReportData = (chartImages, chartSelections = null) => ({
    programInfo,
    executiveSummary: sections.executiveSummary,
    executiveSummaryQuote: sections.executiveSummaryQuote,
    executiveSummaryQuoteAttribution: sections.executiveSummaryQuoteAttribution,
    executiveSummaryLinks: sections.executiveSummaryLinks,
    progressNarrative: sections.progressNarrative,
    progressNarrativeLinks: sections.progressNarrativeLinks,
    progressHighlightBox: sections.progressHighlightBox,
    progressQuote: sections.progressQuote,
    progressQuoteAttribution: sections.progressQuoteAttribution,
    areaOfWorkProgress: buildAowProgress(),
    partnershipOverview: sections.partnershipOverview,
    partnershipHighlightBox: sections.partnershipHighlightBox,
    partnershipQuote: sections.partnershipQuote,
    partnershipQuoteAttribution: sections.partnershipQuoteAttribution,
    partnershipOverviewLinks: sections.partnershipOverviewLinks,
    acceleratorSupport: sections.acceleratorSupport,
    portfolioLinkagesOverview: sections.portfolioLinkagesOverview,
    portfolioLinkagesHighlightBox: sections.portfolioLinkagesHighlightBox,
    portfolioLinkagesQuote: sections.portfolioLinkagesQuote,
    portfolioLinkagesQuoteAttribution: sections.portfolioLinkagesQuoteAttribution,
    portfolioLinkagesOverviewLinks: sections.portfolioLinkagesOverviewLinks,
    adaptiveEvidence: sections.adaptiveEvidence,
    adaptiveResources: sections.adaptiveResources,
    adaptiveBilaterals: sections.adaptiveBilaterals,
    adaptiveRisk: sections.adaptiveRisk,
    adaptiveScaling: sections.adaptiveScaling,
    adaptivePrioritization: sections.adaptivePrioritization,
    adaptiveRiskTable: sections.adaptiveRiskTable,
    keyResultsStories: sections.keyResultsStories,
    innovationNarrative: sections.innovationNarrative,
    stats,
    filters,
    filteredResults,
    tocData,
    trafficLights,
    isAccelerator: reportType === 'accelerator',
    chartImages: chartImages || null,
    chartSelections,
    sectionTabs,
    reportMode,
    // Portfolio Narrative section content
    portfolioOverview: sections.portfolioOverview,
    impactAreaGender: sections.impactAreaGender,
    impactAreaClimate: sections.impactAreaClimate,
    impactAreaNutrition: sections.impactAreaNutrition,
    impactAreaBiodiversity: sections.impactAreaBiodiversity,
    impactAreaPoverty: sections.impactAreaPoverty,
    regionalAfrica: sections.regionalAfrica,
    regionalAmericas: sections.regionalAmericas,
    regionalAsia: sections.regionalAsia,
    portfolioPartnerships: sections.portfolioPartnerships,
    perfProjectedBenefits: sections.perfProjectedBenefits,
    perfInnovationManagement: sections.perfInnovationManagement,
    perfAdaptiveManagement: sections.perfAdaptiveManagement,
    perfMraTracking: sections.perfMraTracking,
  });

  const captureChartsAndExport = async (exportFn, { masterSection = null, chartSelections = null } = {}) => {
    setExportRequest({ format: 'export', masterSection, chartSelections });
    try {
      const chartImages = await requestChartCapture();
      await exportFn(chartImages, chartSelections);
    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed. Please try again.');
    }
    setExportRequest(null);
  };

  // Open chart selection modal for the given format
  const openChartModal = (format) => {
    setChartModal({ open: true, format });
  };

  // Handle confirmed chart selections from modal
  const handleChartSelectionConfirm = (selections) => {
    const format = chartModal.format;
    setChartModal({ open: false, format: null });

    if (format === 'docx') {
      captureChartsAndExport(async (chartImages, sel) => {
        const { generateDocx } = await import('../utils/exportService');
        await generateDocx(buildReportData(chartImages, sel));
      }, { chartSelections: selections });
    } else if (format === 'pdf') {
      captureChartsAndExport(async (chartImages, sel) => {
        const { generatePdf } = await import('../utils/exportService');
        await generatePdf(buildReportData(chartImages, sel));
      }, { chartSelections: selections });
    } else if (format === 'pdfPreview') {
      captureChartsAndExport(async (chartImages, sel) => {
        const { generatePdf } = await import('../utils/exportService');
        const result = await generatePdf({ ...buildReportData(chartImages, sel), preview: true });
        setPdfPreview({ open: true, url: result.blobUrl, filename: result.filename });
      }, { chartSelections: selections });
    }
  };

  // Dev-only master export: captures ALL chart tabs for the current section
  const handleMasterExportDocx = () => captureChartsAndExport(async (chartImages) => {
    const { generateDocx } = await import('../utils/exportService');
    await generateDocx({ ...buildReportData(chartImages), masterSection: activeSection });
  }, { masterSection: activeSection });

  const handleMasterPreviewPdf = () => captureChartsAndExport(async (chartImages) => {
    const { generatePdf } = await import('../utils/exportService');
    const result = await generatePdf({ ...buildReportData(chartImages), masterSection: activeSection, preview: true });
    setPdfPreview({ open: true, url: result.blobUrl, filename: result.filename });
  }, { masterSection: activeSection });

  const handleMasterExportPdf = () => captureChartsAndExport(async (chartImages) => {
    const { generatePdf } = await import('../utils/exportService');
    await generatePdf({ ...buildReportData(chartImages), masterSection: activeSection });
  }, { masterSection: activeSection });

  const closePdfPreview = () => {
    if (pdfPreview.url) URL.revokeObjectURL(pdfPreview.url);
    setPdfPreview({ open: false, url: null, filename: null });
  };

  // Download combined PRMS Results + ToC data as a multi-sheet XLSX
  const handleDownloadCombinedData = async () => {
    setDataExporting(true);
    try {
      const [XLSX, { parsePartnerDetails, parseContributingCenters, parseFunders }] = await Promise.all([
        import('xlsx'),
        import('../utils/dataProcessor/partnershipParsers'),
      ]);
      const wb = XLSX.utils.book_new();

      // Sheet 1: PRMS Results (filtered, sorted by Result Code ascending) — full column set
      if (filteredResults?.length > 0) {
        const sortedResults = [...filteredResults].sort((a, b) => (Number(a.resultCode) || 0) - (Number(b.resultCode) || 0));
        const wsResults = XLSX.utils.json_to_sheet(mapExportRows(sortedResults));
        XLSX.utils.book_append_sheet(wb, wsResults, 'PRMS Results');
      }

      // Sheet 2: ToC Framework data (filtered by selected program, sorted)
      if (tocData && Object.keys(tocData).length > 0) {
        const tocRows = [];
        const tocProgramCodes = filters.program !== 'all'
          ? [filters.program]
          : Object.keys(tocData).sort();
        for (const programCode of tocProgramCodes) {
          const programToc = tocData[programCode];
          if (programToc?.indicators) {
            for (const ind of programToc.indicators) {
              tocRows.push({
                'Program Code': programCode,
                'Area of Work': ind.aow || '',
                'Result Type': ind.resultType || '',
                'Short Title': ind.shortTitle || '',
                'Result Statement': ind.statement || '',
                'Indicator': ind.indicator || '',
                'Type': ind.type || '',
                'Unit': ind.unit || '',
                'Data Source': ind.dataSource || '',
                'Baseline': ind.baseline || '',
                'Target 2025': ind.target2025 || '',
                'Target 2026': ind.target2026 || '',
                'Target 2027': ind.target2027 || '',
                'Target 2028': ind.target2028 || '',
                'Target 2029': ind.target2029 || '',
                'Target 2030': ind.target2030 || '',
                'Total Target': ind.totalTarget || '',
              });
            }
          }
        }
        tocRows.sort((a, b) =>
          a['Program Code'].localeCompare(b['Program Code']) ||
          a['Area of Work'].localeCompare(b['Area of Work'])
        );
        if (tocRows.length > 0) {
          const wsToc = XLSX.utils.json_to_sheet(tocRows);
          XLSX.utils.book_append_sheet(wb, wsToc, 'ToC Framework');
        }
      }

      // Compute traffic light data — per-program when "all", pre-computed when single program
      const tlData = [];
      if (filters.program !== 'all') {
        if (trafficLights?.computed?.length > 0) {
          tlData.push({ code: filters.program, aows: trafficLights.computed, overrides: trafficLights.overrides || {} });
        }
      } else if (tocData && Object.keys(tocData).length > 0) {
        const allFiltered = filterResults(resultsData, { ...filters, program: 'all' });
        for (const [code, programToc] of Object.entries(tocData).sort(([a], [b]) => a.localeCompare(b))) {
          const programResults = allFiltered.filter(r => r.initiative === code);
          if (programResults.length === 0) continue;
          const { aows } = calculateTrafficLights(programToc, programResults, trafficLights.year, allFiltered, filters.resultScope || 'all');
          tlData.push({ code, aows, overrides: {} });
        }
      }

      // Sheet 3: Traffic Lights — AoW summary + per-indicator detail (with Program Code)
      if (tlData.length > 0) {
        const tlRows = [];
        for (const { code, aows, overrides } of tlData) {
          const sortedAows = [...(aows || [])].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          sortedAows.forEach(aow => {
            const override = overrides?.[aow.name];
            const rating = override ? override.rating : aow.rating;
            tlRows.push({
              'Program Code': code,
              'Row Type': 'Area of Work',
              'Area of Work': aow.name,
              'HLO Short Title': '',
              'Indicator': '',
              'Rating': RATING_LABELS[rating] || 'No data',
              'Target': '',
              'Achieved': '',
              'Achieved (W1/W2)': '',
              'Achieved (W3/Bilat)': '',
              'Achievement %': aow.achievementPct != null ? `${Math.round(aow.achievementPct)}%` : `${aow.progress || 0}%`,
              'Matched Results': aow.resultCount || 0,
              'Override': override ? 'Yes' : 'No',
              'Override Justification': override?.justification || '',
            });
            if (aow.indicators) {
              aow.indicators.forEach(ind => {
                tlRows.push({
                  'Program Code': code,
                  'Row Type': 'Indicator',
                  'Area of Work': aow.name,
                  'HLO Short Title': ind.shortTitle || '',
                  'Indicator': ind.indicator || '',
                  'Rating': RATING_LABELS[ind.rating] || '',
                  'Target': ind.target != null ? ind.target : '',
                  'Achieved': ind.achieved != null ? ind.achieved : '',
                  'Achieved (W1/W2)': ind.achievedW1W2 != null ? ind.achievedW1W2 : '',
                  'Achieved (W3/Bilat)': ind.achievedW3 != null ? ind.achievedW3 : '',
                  'Achievement %': ind.achievementPct != null ? `${Math.round(ind.achievementPct)}%` : '',
                  'Matched Results': ind.matchedResultIds?.size || 0,
                  'Override': '',
                  'Override Justification': '',
                });
              });
            }
          });
        }
        if (tlRows.length > 0) {
          const wsTL = XLSX.utils.json_to_sheet(tlRows);
          XLSX.utils.book_append_sheet(wb, wsTL, 'Traffic Lights');
        }
      }

      // Sheet 4: Indicator-Result Matching — one row per result-indicator match (with Program Code, sorted)
      if (tlData.length > 0) {
        const matchRows = [];
        const allResults = filters.program !== 'all'
          ? filteredResults
          : filterResults(resultsData, { ...filters, program: 'all' });
        const resultLookup = new Map((allResults || []).map(r => [String(r.resultCode), r]));

        for (const { code, aows } of tlData) {
          (aows || []).forEach(aow => {
            (aow.indicators || []).forEach(ind => {
              if (!ind.matchedResultIds?.size) return;
              for (const [resultId, contribution] of ind.matchedResultIds) {
                const r = resultLookup.get(String(resultId));
                matchRows.push({
                  'Program Code': code,
                  'Area of Work': aow.name,
                  'HLO Short Title': ind.shortTitle || '',
                  'Indicator': ind.indicator || '',
                  'Indicator Target': ind.target != null ? ind.target : '',
                  'Indicator Achieved': ind.achieved != null ? ind.achieved : '',
                  'Indicator Achievement %': ind.achievementPct != null ? `${Math.round(ind.achievementPct)}%` : '',
                  'Result Code': resultId,
                  'Result Title': r?.title || '',
                  'Result Type': r?.type || '',
                  'Result Initiative': r?.initiative || '',
                  'Result Funding Type': r?.fundingType || '',
                  'Contribution Value': contribution,
                });
              }
            });
          });
        }
        matchRows.sort((a, b) =>
          (a['Program Code'] || '').localeCompare(b['Program Code'] || '') ||
          (a['Result Initiative'] || '').localeCompare(b['Result Initiative'] || '') ||
          (a['Area of Work'] || '').localeCompare(b['Area of Work'] || '')
        );
        if (matchRows.length > 0) {
          const wsMatch = XLSX.utils.json_to_sheet(matchRows);
          XLSX.utils.book_append_sheet(wb, wsMatch, 'Indicator-Result Matching');
        }
      }

      // Sheets 5-7: Parsed Partners, Centers, Funders
      if (filteredResults?.length > 0) {
        const partnerMap = parsePartnerDetails(filteredResults);
        if (partnerMap.size > 0) {
          const partnerRows = [...partnerMap.values()]
            .sort((a, b) => b.resultCount - a.resultCount)
            .map(p => ({
              'Partner Acronym': p.acronym,
              'Full Name': p.fullName,
              'Delivery Types': Array.isArray(p.deliveryTypes) ? p.deliveryTypes.join(', ') : '',
              'Result Count': p.resultCount,
            }));
          XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(partnerRows), 'Parsed Partners');
        }

        const centerMap = parseContributingCenters(filteredResults);
        if (centerMap.size > 0) {
          const centerRows = [...centerMap.values()]
            .sort((a, b) => b.resultCount - a.resultCount)
            .map(c => ({
              'Center Code': c.code,
              'Full Name': c.fullName,
              'Is Primary': c.isPrimary ? 'Yes' : 'No',
              'Result Count': c.resultCount,
            }));
          XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(centerRows), 'Parsed Centers');
        }

        const funderMap = parseFunders(filteredResults);
        if (funderMap.size > 0) {
          const funderRows = [...funderMap.values()]
            .sort((a, b) => b.resultCount - a.resultCount)
            .map(f => ({
              'Funder Name': f.funderName,
              'Grant Count': f.grantCount,
              'Result Count': f.resultCount,
              'Grant Titles': Array.isArray(f.grants) ? f.grants.map(g => `[${g}]`).join(' ') : '',
            }));
          XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(funderRows), 'Parsed Funders');
        }
      }

      // Sheet 8: Stats Summary
      if (stats) {
        const statsRows = [];
        statsRows.push({ Metric: 'Total Results', Value: stats.total || 0 });
        if (stats.byLevel) {
          Object.entries(stats.byLevel).forEach(([k, v]) => statsRows.push({ Metric: `Level: ${k}`, Value: v }));
        }
        if (stats.byType) {
          Object.entries(stats.byType).forEach(([k, v]) => statsRows.push({ Metric: `Type: ${k}`, Value: v }));
        }
        if (stats.byFundingType) {
          statsRows.push({ Metric: 'Pooled (W1/W2)', Value: stats.byFundingType.pooled || 0 });
          statsRows.push({ Metric: 'Non-pooled (W3)', Value: stats.byFundingType.nonpooled || 0 });
        }
        statsRows.push({ Metric: 'Countries', Value: stats.countries?.length || 0 });
        const wsStats = XLSX.utils.json_to_sheet(statsRows);
        XLSX.utils.book_append_sheet(wb, wsStats, 'Stats Summary');
      }

      const filename = `${programInfo.name || 'cgiar'}_combined_data_${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, filename);
    } catch (error) {
      console.error('Data export error:', error);
      alert('Data export failed. Please try again.');
    }
    setDataExporting(false);
  };

  return (
    <header className="sticky top-0 bg-[var(--surface-primary)] border-b border-[var(--border-primary)] z-50">
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-4">
          <button
            onClick={onToggleSidebar}
            className="lg:hidden p-2 hover:bg-[var(--surface-hover)] rounded-lg"
          >
            <Menu className="w-5 h-5 text-[var(--text-secondary)]" />
          </button>

          <div className="flex items-center gap-3">
            <img src="/cgiar-logo.svg" alt="CGIAR" className="h-10 w-auto" />
            <div>
              <h1 className="text-lg font-bold text-[var(--text-primary)]">CGIAR Zero Draft Report Assistant</h1>
              <p className="text-xs text-[var(--text-tertiary)]">Performance & Results Management System</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-3">
          {/* Combined data download */}
          <button
            onClick={handleDownloadCombinedData}
            disabled={dataExporting || !filteredResults?.length}
            className="flex items-center gap-2 px-2 sm:px-3 py-2 text-sm text-[var(--text-secondary)] hover:text-cgiar-green hover:bg-cgiar-green-tint border border-[var(--border-primary)] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Download PRMS Results + ToC data as multi-sheet Excel"
          >
            <Database className="w-4 h-4" />
            <span className="hidden sm:inline">{dataExporting ? 'Exporting...' : 'Full Data'}</span>
          </button>

          {/* DOCX dropdown */}
          <div className="relative" ref={docxMenuRef}>
            <button
              onClick={() => { setDocxMenuOpen(!docxMenuOpen); setPdfMenuOpen(false); setDevMenuOpen(false); }}
              disabled={isExporting || !filteredResults?.length}
              className="flex items-center gap-2 px-2.5 sm:px-4 py-2 text-sm bg-cgiar-green text-white hover:bg-cgiar-green-dark rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">DOCX</span>
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {docxMenuOpen && (
              <div className="absolute top-full right-0 mt-1 w-40 bg-[var(--surface-primary)] border border-[var(--border-primary)] rounded-lg shadow-lg z-50 py-1">
                <button
                  onClick={() => { setDocxMenuOpen(false); openChartModal('docx'); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--surface-hover)] transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </div>
            )}
          </div>

          {/* PDF dropdown */}
          <div className="relative" ref={pdfMenuRef}>
            <button
              onClick={() => { setPdfMenuOpen(!pdfMenuOpen); setDocxMenuOpen(false); setDevMenuOpen(false); }}
              disabled={isExporting || !filteredResults?.length}
              className="flex items-center gap-2 px-2.5 sm:px-4 py-2 text-sm text-[var(--text-secondary)] hover:text-cgiar-green hover:bg-cgiar-green-tint border border-[var(--border-primary)] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">PDF</span>
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {pdfMenuOpen && (
              <div className="absolute top-full right-0 mt-1 w-40 bg-[var(--surface-primary)] border border-[var(--border-primary)] rounded-lg shadow-lg z-50 py-1">
                <button
                  onClick={() => { setPdfMenuOpen(false); openChartModal('pdfPreview'); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--surface-hover)] transition-colors"
                >
                  <Eye className="w-4 h-4" />
                  Preview
                </button>
                <button
                  onClick={() => { setPdfMenuOpen(false); openChartModal('pdf'); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--surface-hover)] transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </div>
            )}
          </div>

          {/* Dev-only: Per-section master export (all chart tabs for current section) */}
          {import.meta.env.DEV && (() => {
            const DEV_SECTION_LABELS = { overview: 'Sec 1: Overview', progress: 'Sec 2: Progress', partnerships: 'Sec 3: Partnerships' };
            const devLabel = DEV_SECTION_LABELS[activeSection];
            if (!devLabel) return null;
            return (
              <div className="relative" ref={devMenuRef}>
                <button
                  onClick={() => { setDevMenuOpen(!devMenuOpen); setDocxMenuOpen(false); setPdfMenuOpen(false); }}
                  disabled={isExporting || !filteredResults?.length}
                  className="flex items-center gap-2 px-2 sm:px-3 py-2 text-sm text-amber-600 hover:text-amber-700 hover:bg-amber-50 border border-amber-300 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title={`Dev-only: Export all chart tabs for ${devLabel}`}
                >
                  <Wrench className="w-4 h-4" />
                  <span className="hidden sm:inline">DEV</span>
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                {devMenuOpen && (
                  <div className="absolute top-full right-0 mt-1 w-60 bg-[var(--surface-primary)] border border-amber-300 rounded-lg shadow-lg z-50 py-1">
                    <div className="px-3 py-1.5 text-xs text-amber-600 font-medium border-b border-amber-200">
                      All Charts — {devLabel}
                    </div>
                    <button
                      onClick={() => { setDevMenuOpen(false); handleMasterExportDocx(); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--surface-hover)] transition-colors"
                    >
                      <FileText className="w-4 h-4" />
                      DOCX — {devLabel}
                    </button>
                    <button
                      onClick={() => { setDevMenuOpen(false); handleMasterPreviewPdf(); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--surface-hover)] transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                      PDF Preview — {devLabel}
                    </button>
                    <button
                      onClick={() => { setDevMenuOpen(false); handleMasterExportPdf(); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--surface-hover)] transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      PDF Download — {devLabel}
                    </button>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      <PdfPreviewModal
        isOpen={pdfPreview.open}
        onClose={closePdfPreview}
        pdfUrl={pdfPreview.url}
        filename={pdfPreview.filename}
      />

      {chartModal.open && createPortal(
        <ChartSelectionModal
          format={chartModal.format === 'pdfPreview' ? 'pdf' : chartModal.format}
          onConfirm={handleChartSelectionConfirm}
          onCancel={() => setChartModal({ open: false, format: null })}
        />,
        document.body
      )}
    </header>
  );
}
